import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Minus, Plus, Trash2, Building2, User, Mail, Phone, MapPin, Calendar, FileText } from "lucide-react";
import api from "@/lib/api";
import { useQuoteCart } from "@/context/QuoteCartContext";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_bakery-b2b/artifacts/klxy49l5_Karpatka.png";

const BUSINESS_TYPES = ["Hotel", "Restaurant", "Café", "Resort & Spa", "Catering", "Distributor", "Manufacturer", "Corporate hospitality", "Other"];
const VOLUMES = [
  "Less than 50 desserts / month",
  "50 – 150 desserts / month",
  "150 – 300 desserts / month",
  "300+ desserts / month",
];
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export default function QuoteRequest() {
  const { items, setQuantity, removeItem, clear } = useQuoteCart();
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    business_type: "",
    city: "",
    state: "",
    estimated_volume: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your quote cart is empty."); return; }
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          size_label: i.size_label,
          quantity: i.quantity,
        })),
      });
      setSuccess(true);
      clear();
      toast.success("Quote submitted — we'll be in touch within one business day.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="quote-success-page" className="max-w-3xl mx-auto px-6 py-24">
        <div className="bg-white border border-brand-line rounded-[2rem] shadow-soft-lg p-10 md:p-16 text-center relative overflow-hidden">
          <img src={LOGO} alt="Cake World" className="h-24 w-auto mx-auto object-contain relative" />
          <div className="w-14 h-14 mx-auto mt-8 rounded-full bg-brand-red grid place-items-center relative">
            <CheckCircle2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl text-brand-ink relative">Quote request received</h1>
          <p className="mt-4 text-brand-stone font-ui max-w-md mx-auto relative">
            Thank you. Our wholesale team will reply within one business day at the email you provided with a personalized quote and lead times.
          </p>
          <div className="mt-4 text-xs tracking-[0.25em] uppercase text-brand-pink font-ui font-bold relative">
            Every occasion needs a sweet touch
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3 relative">
            <Link to="/catalog" data-testid="success-catalog" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition">
              Continue browsing
            </Link>
            <button onClick={() => nav("/")} className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-brand-ink text-brand-ink text-sm font-ui font-semibold hover:bg-brand-ink hover:text-white transition">
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="request-quote-page" className="max-w-7xl mx-auto px-6 md:px-10 py-14">
      {/* Header with logo */}
      <div className="flex items-center gap-4 mb-10">
        <img src={LOGO} alt="Cake World" className="h-16 w-auto object-contain" />
        <div className="border-l border-brand-line pl-4 hidden sm:block">
          <div className="eyebrow">Wholesale inquiry</div>
          <div className="font-display text-xl text-brand-ink italic">Request your quote</div>
        </div>
      </div>

      <div className="max-w-2xl">
        <h1 className="font-display text-5xl sm:text-6xl text-brand-ink leading-none">Let's get you a quote.</h1>
        <p className="mt-6 text-brand-stone leading-relaxed font-ui text-lg">
          Share a few details about your business — our team returns a personalized wholesale quote within one business day.
        </p>
      </div>

      <div className="dot-divider mt-10" />

      <div className="mt-10 grid lg:grid-cols-5 gap-10">
        {/* Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-8" data-testid="quote-form">
          {/* Section: Business */}
          <div className="bg-white rounded-3xl border border-brand-line shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center"><Building2 className="w-5 h-5" /></div>
              <div>
                <div className="eyebrow">Step 01</div>
                <h2 className="font-display text-2xl text-brand-ink leading-none mt-1">Business details</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Company name" required value={form.company_name} onChange={set("company_name")} testId="field-company" placeholder="Grand Hotel Group" />
              <div>
                <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Business type <span className="text-brand-red">*</span></label>
                <select
                  value={form.business_type}
                  onChange={set("business_type")}
                  required
                  data-testid="field-business-type"
                  className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red"
                >
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <Field label="City" value={form.city} onChange={set("city")} testId="field-city" placeholder="Phoenix" />
              <div>
                <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">State</label>
                <select
                  value={form.state}
                  onChange={set("state")}
                  data-testid="field-state"
                  className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red"
                >
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Section: Contact */}
          <div className="bg-white rounded-3xl border border-brand-line shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center"><User className="w-5 h-5" /></div>
              <div>
                <div className="eyebrow">Step 02</div>
                <h2 className="font-display text-2xl text-brand-ink leading-none mt-1">Point of contact</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Contact name" required value={form.contact_name} onChange={set("contact_name")} testId="field-contact" placeholder="Jane Doe" />
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="field-email" placeholder="jane@grandhotel.com" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testId="field-phone" placeholder="+1 (623) 324-7530" />
              <div>
                <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Estimated monthly volume</label>
                <select
                  value={form.estimated_volume}
                  onChange={set("estimated_volume")}
                  data-testid="field-volume"
                  className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red"
                >
                  <option value="">Select…</option>
                  {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Section: Notes */}
          <div className="bg-white rounded-3xl border border-brand-line shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center"><FileText className="w-5 h-5" /></div>
              <div>
                <div className="eyebrow">Step 03</div>
                <h2 className="font-display text-2xl text-brand-ink leading-none mt-1">Anything else we should know?</h2>
              </div>
            </div>
            <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={set("notes")}
              data-testid="field-notes"
              rows={5}
              placeholder="Delivery window, packaging preferences, seasonal needs, allergens…"
              className="mt-1.5 w-full rounded-xl bg-white border border-brand-line px-3 py-2.5 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || items.length === 0}
            data-testid="submit-quote"
            className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-brand-red text-white text-base font-ui font-semibold hover:bg-brand-redDark disabled:opacity-50 transition active:scale-[0.98]"
          >
            {submitting ? "Submitting…" : "Submit quote request"} <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-brand-stone text-center font-ui">
            By submitting you agree to be contacted by our wholesale team regarding your inquiry.
          </p>
        </form>

        {/* Sticky summary */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div className="bg-brand-ink text-white rounded-3xl p-8 relative overflow-hidden">
              <div className="relative">
                <div className="eyebrow text-brand-pink">Your quote cart</div>
                <h2 className="font-display text-3xl mt-2">{items.length} item{items.length !== 1 ? "s" : ""}</h2>

                {items.length === 0 ? (
                  <div className="mt-6 text-white/70 text-sm font-ui">
                    Your cart is empty.{" "}
                    <Link to="/catalog" className="underline text-brand-pink">Add products</Link>
                  </div>
                ) : (
                  <ul className="mt-6 space-y-3 max-h-[420px] overflow-y-auto pr-2">
                    {items.map((i) => (
                      <li key={`${i.product_id}-${i.size_label}`} className="flex gap-3 items-center bg-white/5 rounded-2xl p-3" data-testid={`summary-item-${i.product_id}-${i.size_label}`}>
                        <img src={i.image_url} alt="" className="w-14 h-14 rounded-xl object-contain bg-white" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium line-clamp-1 font-ui">{i.product_name}</div>
                          <div className="text-xs text-white/60 font-ui">{i.size_label} {i.size_label === '3"' ? "Mini" : "Whole"}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setQuantity(i.product_id, i.size_label, i.quantity - 1)} className="w-7 h-7 rounded-full bg-white/10 grid place-items-center hover:bg-white/20"><Minus className="w-3 h-3"/></button>
                          <span className="w-6 text-center text-xs font-ui">{i.quantity}</span>
                          <button type="button" onClick={() => setQuantity(i.product_id, i.size_label, i.quantity + 1)} className="w-7 h-7 rounded-full bg-white/10 grid place-items-center hover:bg-white/20"><Plus className="w-3 h-3"/></button>
                          <button type="button" onClick={() => removeItem(i.product_id, i.size_label)} className="ml-1 w-7 h-7 rounded-full text-white/50 hover:text-brand-pink"><Trash2 className="w-3 h-3"/></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="bg-brand-pinkSoft border border-brand-line rounded-3xl p-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-brand-red mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-brand-ink font-ui">1 business day turnaround</div>
                  <p className="mt-1 text-xs text-brand-stone font-ui leading-relaxed">
                    You'll receive pricing, lead times and packaging options by email — no obligation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">
        {label} {required && <span className="text-brand-red">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1.5 w-full h-12 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red placeholder:text-brand-stoneLight"
      />
    </div>
  );
}
